import React, { useState } from 'react';
import { ArrowUpRight, Mail, MapPin, Phone } from 'lucide-react';
import axios from 'axios';
import { brand, interests, images } from '../mock';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  firstName: '', lastName: '', organisation: '', email: '',
  interests: [], message: '', subscribe: false, consent: false
};

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const toggleInterest = (i) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i]
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.organisation || !form.email) {
      toast({ title: 'A few fields to complete', description: 'Please add your name, organisation and email.' });
      return;
    }
    if (!form.consent) {
      toast({ title: 'Consent needed', description: 'Please confirm processing of your details to proceed.' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast({ title: 'Thanks — message received', description: 'We\u2019ll get back to you shortly. Speak soon.' });
      setForm(initialForm);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({ title: 'Couldn\u2019t send that', description: typeof detail === 'string' ? detail : 'Please try again in a moment.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pt-32">
      <section className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[#6B8E4E]">Talk to us</div>
            <h1 className="font-serif-display text-6xl md:text-8xl text-[#1E3A2B] mt-6 leading-[0.98] tracking-[-0.02em]">
              Let’s start a<br /> <span className="italic text-[#6B8E4E]">conversation</span>.
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-[17px] text-[#3d4a41] leading-relaxed">
              For coaching, leadership, consultancy and other enquiries, tell us a little about you
              and how we can support you, and we&rsquo;ll come back with the next best step.
            </p>
          </div>
        </div>
      </section>

      <section className="mt-20 pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <aside className="md:col-span-4 space-y-8">
            <div className="rounded-3xl border border-[#E7DFCF] p-8 bg-[#FDFAF2]">
              <div className="text-[11px] tracking-[0.24em] uppercase text-[#6B8E4E]">Direct</div>
              <ul className="mt-6 space-y-5 text-[15px] text-[#1E3A2B]">
                <li className="flex items-start gap-3"><Mail className="w-4 h-4 mt-1 text-[#E27B47]" /><a href={`mailto:${brand.email}`} className="link-underline">{brand.email}</a></li>
                <li className="flex items-start gap-3"><Phone className="w-4 h-4 mt-1 text-[#E27B47]" />{brand.phone}</li>
                <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-1 text-[#E27B47]" />{brand.location}</li>
              </ul>
            </div>

            <div className="rounded-3xl bg-[#1E3A2B] text-[#EEE7D3] p-8">
              <div className="text-[11px] tracking-[0.24em] uppercase text-[#B8CE7F]">Response</div>
              <p className="font-serif-display text-3xl mt-3 leading-tight">
                We reply within two working days — usually sooner.
              </p>
            </div>

            <div className="rounded-3xl overflow-hidden aspect-[4/5] relative">
              <div
                className="absolute top-1/2 left-1/2 w-[125%] aspect-[5/4]"
                style={{ transform: 'translate(-50%, -50%) rotate(-90deg)' }}
              >
                <img
                  src="https://customer-assets-4nw71qhi.emergentagent.net/job_dragon-dev/artifacts/im1934c1_DSC_2631.webp"
                  alt="Byron"
                  className="w-full h-full object-cover image-zoom"
                />
              </div>
            </div>
          </aside>

          <form onSubmit={onSubmit} className="md:col-span-8 rounded-3xl border border-[#E7DFCF] p-8 md:p-12 bg-[#FDFAF2]">
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="First name *" value={form.firstName} onChange={(v) => setForm({ ...form, firstName: v })} />
              <Field label="Last name *" value={form.lastName} onChange={(v) => setForm({ ...form, lastName: v })} />
              <Field label="Organisation *" value={form.organisation} onChange={(v) => setForm({ ...form, organisation: v })} />
              <Field label="Email address *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            </div>

            <div className="mt-8">
              <div className="text-[11px] tracking-[0.24em] uppercase text-[#6B8E4E] mb-4">What are you most interested in?</div>
              <div className="flex flex-wrap gap-2">
                {interests.map((i) => {
                  const active = form.interests.includes(i);
                  return (
                    <button
                      type="button"
                      key={i}
                      onClick={() => toggleInterest(i)}
                      className={`btn-transition rounded-full px-4 py-2 text-[13px] border ${
                        active
                          ? 'bg-[#1E3A2B] text-[#FAF6EE] border-[#1E3A2B]'
                          : 'bg-transparent text-[#1E3A2B] border-[#CFC6AF] hover:border-[#1E3A2B]'
                      }`}
                    >
                      {i}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-8">
              <label className="block text-[11px] tracking-[0.24em] uppercase text-[#6B8E4E] mb-2">Message / comments</label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={6}
                className="w-full bg-transparent border border-[#CFC6AF] focus:border-[#1E3A2B] rounded-2xl p-4 text-[15px] outline-none btn-transition resize-none"
                placeholder="Tell us a little about your challenge or hope..."
              />
            </div>

            <div className="mt-8 space-y-4">
              <label className="flex items-start gap-3 text-[14px] text-[#3d4a41]">
                <input type="checkbox" checked={form.subscribe} onChange={(e) => setForm({ ...form, subscribe: e.target.checked })} className="mt-1 accent-[#1E3A2B]" />
                Add me to the mailing list for updates and free resources.
              </label>
              <label className="flex items-start gap-3 text-[14px] text-[#3d4a41]">
                <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 accent-[#1E3A2B]" />
                I consent to my details being processed in line with the privacy policy.
              </label>
            </div>

            <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
              <div className="text-[12px] text-[#6B8E4E] tracking-wide">We treat your details with care.</div>
              <button
                type="submit"
                disabled={submitting}
                className="btn-transition inline-flex items-center gap-2 bg-[#E27B47] hover:bg-[#D06A38] disabled:opacity-70 text-[#FAF6EE] px-7 py-3.5 rounded-full text-[14px]"
              >
                {submitting ? 'Sending...' : 'Let\u2019s get started!'} <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
};

const Field = ({ label, value, onChange, type = 'text' }) => (
  <div>
    <label className="block text-[11px] tracking-[0.24em] uppercase text-[#6B8E4E] mb-2">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-transparent border-b border-[#CFC6AF] focus:border-[#1E3A2B] py-3 text-[15px] outline-none btn-transition"
    />
  </div>
);

export default Contact;
